import { useCallback, useEffect, useRef } from 'react'
import { Editor as TipTapEditor } from '@tiptap/react'
import { Editor as SlateEditor } from 'slate-react'
import { useDictationContext } from '../../../core/provider/dictation-context.provider'

export const useDictationTipTapTarget = (editor: TipTapEditor | null) => {
  const { registerTipTapEditor, setActiveTarget } = useDictationContext()

  useEffect(() => {
    registerTipTapEditor(editor)

    if (editor) {
      const handleFocus = () => {
        setActiveTarget({
          type: 'tiptap',
          element: editor.view?.dom ?? null,
          tiptapEditor: editor,
        })
      }

      editor.on('focus', handleFocus)
      return () => {
        editor.off('focus', handleFocus)
      }
    }
  }, [editor, registerTipTapEditor, setActiveTarget])
}

export const useDictationSlateTarget = (
  editor: SlateEditor | null,
  insertTextCallback?: (text: string) => void,
) => {
  const { registerSlateEditor, registerSlateInsertCallback, setActiveTarget } =
    useDictationContext()
  const editorRef = useRef(editor)
  editorRef.current = editor

  useEffect(() => {
    registerSlateEditor(editor)
  }, [editor, registerSlateEditor])

  useEffect(() => {
    registerSlateInsertCallback(insertTextCallback ?? null)
    return () => registerSlateInsertCallback(null)
  }, [insertTextCallback, registerSlateInsertCallback])

  const handleFocus = useCallback(() => {
    if (editorRef.current) {
      setActiveTarget({
        type: 'slate',
        element: null,
        slateEditor: editorRef.current,
      })
    }
  }, [setActiveTarget])

  return { handleFocus }
}

export const useDictationNativeTarget = <T extends HTMLElement>() => {
  const ref = useRef<T>(null)
  const { setActiveTarget } = useDictationContext()

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const handleFocus = () => {
      const tagName = element.tagName.toLowerCase()
      let type: 'input' | 'textarea' | 'contenteditable' = 'contenteditable'

      if (tagName === 'input') type = 'input'
      else if (tagName === 'textarea') type = 'textarea'
      else if (element.contentEditable === 'true') type = 'contenteditable'

      setActiveTarget({ type, element })
    }

    element.addEventListener('focus', handleFocus)
    return () => {
      element.removeEventListener('focus', handleFocus)
    }
  }, [setActiveTarget])

  return ref
}
